import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback } from "react";
import { useTranslations } from "../../i18n";
import { IconButton } from "../Button/IconButton";
import { Skeleton } from "../Skeleton";
import { usePaginationContext } from "./context";
import { usePaginationRange } from "./hooks/usePaginationRange";

export const PageControls = () => {
  const { t } = useTranslations();
  const {
    page,
    pageSize,
    totalItems,
    onPageChange,
    loading,
    disabled,
    ariaLabel,
  } = usePaginationContext();
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginationRange = usePaginationRange(page, totalPages);

  const handlePreviousPage = useCallback(() => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  }, [page, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (page < totalPages) {
      onPageChange(page + 1);
    }
  }, [page, totalPages, onPageChange]);

  const handlePageClick = useCallback(
    (pageNumber: number) => {
      onPageChange(pageNumber);
    },
    [onPageChange],
  );

  if (loading) {
    return <Skeleton className="nuco-pagination__skeleton--controls" />;
  }

  return (
    <nav
      aria-label={ariaLabel ?? t("ui.pagination.pagination")}
      className="nuco-pagination__controls"
    >
      <IconButton
        size="xs"
        variant="ghost"
        color="neutral"
        onClick={handlePreviousPage}
        disabled={disabled || page === 1}
        aria-label={t("ui.pagination.goToPrevPage")}
      >
        <ChevronLeft size="0.875rem" />
      </IconButton>

      {paginationRange.map((item, index) => {
        if (item === "...") {
          return (
            <span
              key={`ellipsis-${index}`}
              className="nuco-pagination__ellipsis nuco-text nuco-text--sm nuco-text--medium"
              aria-hidden="true"
            >
              ...
            </span>
          );
        }

        return (
          <IconButton
            key={item}
            size="xs"
            variant={page === item ? "solid" : "ghost"}
            color={page === item ? "brand" : "neutral"}
            onClick={() => handlePageClick(item)}
            disabled={disabled}
            aria-label={t("ui.pagination.goToPage", { pageNumber: item })}
            aria-current={page === item ? "page" : undefined}
            className="nuco-pagination__button"
          >
            {item}
          </IconButton>
        );
      })}

      <IconButton
        size="xs"
        variant="ghost"
        color="neutral"
        onClick={handleNextPage}
        disabled={disabled || page === totalPages}
        aria-label={t("ui.pagination.goToNextPage")}
      >
        <ChevronRight size="0.875rem" />
      </IconButton>
    </nav>
  );
};
